import type {DoppeActionLayoutMediaRatio, DoppeActionLayoutMediaSize} from './doppe-action-layout';
import {
    DoppeActionLayout,
    DoppeActionLayoutMediaDesign,
    doppeActionLayoutGetDefaultMediaSizeForLayout,
    doppeActionLayoutGetDefaultRatioForLayout
} from './doppe-action-layout';
import {DoppeActionListItemClickBehaviour, doppeActionListItemResolveEffectiveClickBehaviour} from './doppe-action-list-item-click-behaviour';
import type {DoppeDtoAction} from './doppe-dto-action';
import {doppeDtoActionGetDefaultSearchListSettingsProps} from './doppe-dto-action';
import type {WixImageResource, WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {BymoPageActionMediaViewLayout, BymoPageActionMediaViewSettings, BymoPageMedia,} from '../bymo-pages';
import type {HideableValue, OmitStrict, PartiallyRequired, PickByValueExact, Values} from '@wix/devzai-utils-common';
import {
    assertDefined,
    hideableValueCreateHidden,
    hideableValueGetValue,
    hideableValueIsVisibleAndNotEqualValue,
    hideableValueModifyValue,
    objectForEachValue
} from '@wix/devzai-utils-common';
import type {DoppeActionCtaButtonSettings} from './doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from './doppe-action-cta-button-settings';
import type {DoppeActionContentSection} from './doppe-action-content-section';
import type {DoppeActionContentSectionsSettings} from './doppe-action-content-sections-settings';
import {
    doppeActionContentSectionDefaultValuesMigration,
    doppeActionContentSectionSettingsDefaultValuesMigration
} from './doppe-action-content-sections-settings';
import {doppeActionGetMediaViewDefaultSettingsMigration} from './doppe-action-media-view-settings';
import type {DoppeActionCTAButton} from './doppe-action-cta-button';
import {
    doppeActionCtaButtonCreateFromListAndOverrideId,
    doppeActionCtaButtonResetSettingsForLockNew,
    DoppeActionCTAType
} from './doppe-action-cta-button';
import type {DoppeActionCtaButtonsSettings} from './doppe-action-cta-buttons-settings';
import {doppeActionCtaButtonsSettingsDefaultValuesMigration} from './doppe-action-cta-buttons-settings';
import {doppeActionContentSectionGetDefaultValues} from "./doppe-action-content-section-utils";

export type DoppeListActionSettings<ItemSettings extends DoppeListActionSettings.ListItemBaseSettings = any> = {
        listDataItems: ItemSettings[];
    }
    & DoppeListActionSettings.CommonSettings
    & DoppeActionCtaButtonsSettings
    & DoppeActionContentSectionsSettings
    & (ItemSettings extends DoppeListActionSettings.ListItemWithSections ? DoppeListActionSettings.WithListItemTemplateSections : {})
    & (ItemSettings extends DoppeListActionSettings.ListItemWithCtaButtons ? DoppeListActionSettings.WithListItemTemplateCtaButtons : {});

export type DoppeListActionPartialSettings<
    TActionSettings extends DoppeListActionSettings<TItemSettings>,
    TItemSettings extends DoppeListActionSettings.ListItemBaseSettings,
    TDeprecatedFields,
    TDeprecatedItemFields
> =
    Partial<OmitStrict<TActionSettings, keyof DoppeListActionSettings<TItemSettings>>> &
    TDeprecatedFields &
    Partial<OmitStrict<DoppeListActionSettings<TItemSettings & TDeprecatedItemFields>, 'listDataItems'> & {
        listDataItems: PartiallyRequired<Partial<TItemSettings & TDeprecatedItemFields>, 'id'>[];
    }>;

export type DoppeListActionPartialSettingsV2<
    ItemSettings extends DoppeListActionSettings.ListItemBaseSettings
> =
    OmitStrict<DoppeListActionSettings<ItemSettings>, 'listDataItems'> & {
    listDataItems: PartiallyRequired<Partial<ItemSettings>, 'id'>[];
};

export const DoppeListActionItemLayout = {
    Default: 'Default',
    FullDetailsWithShowMore: 'FullDetailsWithShowMore',
    FullDetails: 'FullDetails',
} as const;

export type DoppeListActionItemLayout = Values<typeof DoppeListActionItemLayout>;

export const DoppeListActionSettingsContentSectionAutoValue = {
    ListTemplateSections: 'ListTemplateSections',
} as const;

export type DoppeListActionSettingsContentSectionAutoValue = Values<typeof DoppeListActionSettingsContentSectionAutoValue>;


export const DoppeListActionSettingsCtaButtonAutoValue = {
    ListTemplateButtons: 'ListTemplateButtons',
} as const;

export type DoppeListActionSettingsCtaButtonAutoValue = Values<typeof DoppeListActionSettingsCtaButtonAutoValue>;



export namespace DoppeListActionSettings {

    export interface CommonSettings {
        itemShowDescription: boolean;
        itemShowImage: boolean;
        itemShowTitle: boolean;
        itemShowTextOnImage: boolean;
        mediaSize: DoppeActionLayoutMediaSize | null;
        mediaDesign: DoppeActionLayoutMediaDesign;
        mediaRatio: DoppeActionLayoutMediaRatio;
        listItemLayoutType: DoppeListActionItemLayout;
        layout: DoppeActionLayout;
        latestNonPremiumLayout?: DoppeActionLayout;
    }

    export interface ListItemBaseSettings {
        id: string;
        enabled: boolean;
        deleted: boolean;
        displayInSearch: boolean;
        isDummy: boolean;
    }

    export type SectionListItem = DoppeActionContentSection | DoppeListActionSettingsContentSectionAutoValue;

    export type ListItemWithSections = {
        sectionsOverridesMap: Record<string, DoppeActionContentSection.Override>;
        customSections: SectionListItem[];
    }

    export type CtaButtonListItem = DoppeActionCTAButton.MultipleButton | DoppeListActionSettingsCtaButtonAutoValue;

    export type ListItemWithCtaButtons = {
        ctaButtonsOverridesMap: Record<string, DoppeActionCTAButton.MultipleButtonOverride>;
        customCtaButtons: CtaButtonListItem[];
    }

    export interface DeprecatedFields {
        showButton: boolean;
        buttonText: string;
        buttonUrl: string;
    }

    export type ResolveListItemSettings<
        T extends DoppeListActionSettings,
        K extends keyof T & string
    > = T[K] extends Array<infer U> ? U : never;

    export type ResolvePartialAndDeprecatedActionSettings<
        T extends DoppeListActionSettings,
        V extends { id: string },
        K extends keyof PickByValueExact<T, V[]> & string
    > = Partial<OmitStrict<T, K>> & {
        [key in K]?: PartiallyRequired<Partial<V>, 'id'>[]
    } & DoppeListActionSettings.DeprecatedFields;

    export interface WithExternalDataSourceSupport<T extends {}> {
        externalDataSource: null | T;
        externalDataSourceItemsCount: number;
    }

    export interface WithCtaButton extends DoppeActionCtaButtonSettings {

    }

    export interface WithListItemTemplateCtaButtons {
        listItemTemplateCtaButtonsSettings: DoppeActionCtaButtonsSettings;
    }

    export interface WithListItemTemplateSections {
        listItemTemplateSectionsSettings: DoppeActionContentSectionsSettings;
    }

    export interface WithMainDetails {
        mainImage: HideableValue<WixMediaResource | null>;
        mainMediaViewSettings: BymoPageActionMediaViewSettings;
        mainMediaViewLayout: BymoPageActionMediaViewLayout;
        mainTitle: HideableValue<string>;
    }

    export interface WithItemClickBehaviour {
        itemClickBehaviour: DoppeActionListItemClickBehaviour;
        allowItemClickBehaviourPerItem: boolean;
    }

    /**
     * Per-item click behaviour override. When hidden, the item inherits the list-level click behaviour.
     * When visible, the item uses its own custom click behaviour.
     */
    export interface ListItemWithItemClickBehaviour {
        itemClickBehaviour: HideableValue<DoppeActionListItemClickBehaviour>;
    }

    export interface WithSearchSettings {
        listSearchSettings: DoppeDtoAction.ListSearchSettings;
    }
}

export function doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings: DoppeListActionSettings<DoppeListActionSettings.ListItemWithCtaButtons & DoppeListActionSettings.ListItemBaseSettings>) {
    actionSettings.listDataItems.forEach(listItem => {
        listItem.customCtaButtons.forEach(customCtaButton => {
            if (customCtaButton !== DoppeListActionSettingsCtaButtonAutoValue.ListTemplateButtons) {
                doppeActionCtaButtonResetSettingsForLockNew(customCtaButton);
            }
        });

        objectForEachValue(listItem.ctaButtonsOverridesMap, overrideButton => {
            doppeActionCtaButtonResetSettingsForLockNew(overrideButton);
        });
    });

    actionSettings.listItemTemplateCtaButtonsSettings.ctaButtons.forEach(ctaButton => {
        doppeActionCtaButtonResetSettingsForLockNew(ctaButton);
    });
}


export function doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(ctaButtonsSettings: Partial<DoppeListActionSettings.WithListItemTemplateCtaButtons>, hasSourceURL: boolean) {

    const {
        ctaButtons,
    } = ctaButtonsSettings?.listItemTemplateCtaButtonsSettings ?? {}

    return {
        listItemTemplateCtaButtonsSettings: {
            ctaButtons: ctaButtons ? ctaButtons.map(ctaButton => {
                return assertDefined(doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                    ctaButton: ctaButton,
                    hasSourceURL: hasSourceURL
                })).ctaButton
            }) : []
        }
    } satisfies DoppeListActionSettings.WithListItemTemplateCtaButtons;
}

export function doppeListActionTemplateSectionsSettingsDefaultValuesMigration(sectionsSettings: Partial<DoppeListActionSettings.WithListItemTemplateSections>) {

    const {
        sections,
    } = sectionsSettings?.listItemTemplateSectionsSettings ?? {}

    return {
        listItemTemplateSectionsSettings: {
            sections: sections ? sections.map(section => {
                return doppeActionContentSectionDefaultValuesMigration(section);
            }) : []
        }
    } satisfies DoppeListActionSettings.WithListItemTemplateSections;
}

export function doppeListActionListItemWithSectionsMigration(overrides?: Partial<DoppeListActionSettings.ListItemWithSections>) {

    const sectionsOverridesMap = overrides?.sectionsOverridesMap ?? {};
    objectForEachValue(sectionsOverridesMap, (partialContentSection) => {

        const {
            content,
            ...resetPartialContentSection
        } = partialContentSection;

        return {
            content: {
                ...doppeActionContentSectionGetDefaultValues(content.type),
                ...content
            },
            ...resetPartialContentSection
        } satisfies  DoppeActionContentSection.Override
    })

    const customSections = overrides?.customSections ? [...overrides?.customSections] : [DoppeListActionSettingsContentSectionAutoValue.ListTemplateSections]
    const hasListTemplateSections = customSections.includes(DoppeListActionSettingsContentSectionAutoValue.ListTemplateSections);

    if (!hasListTemplateSections) {
        customSections.push(DoppeListActionSettingsContentSectionAutoValue.ListTemplateSections);
    }

    return {
        customSections: customSections.map(listItemSection => {
            if (listItemSection === DoppeListActionSettingsContentSectionAutoValue.ListTemplateSections) {
                return listItemSection;
            } else {
                return doppeActionContentSectionDefaultValuesMigration(listItemSection)
            }
        }),
        sectionsOverridesMap: sectionsOverridesMap,
    } satisfies DoppeListActionSettings.ListItemWithSections;
}

export function doppeListActionListItemCalculateItemSections(sectionsSettings: DoppeActionContentSectionsSettings, itemSettings: DoppeListActionSettings.ListItemWithSections) {

    const sections: DoppeActionContentSection.ContentSectionWithOverride[] = [];

    itemSettings.customSections.forEach(listItemSection => {
        if (listItemSection === DoppeListActionSettingsContentSectionAutoValue.ListTemplateSections) {
            sectionsSettings.sections.forEach(listSection => {
                const override = itemSettings.sectionsOverridesMap[listSection.id];

                const {
                    content,
                    ...restOverrideSettings
                } = override ?? {};

                const {
                    content: listSectionSettings,
                    ...restListCtaButton
                } = listSection

                sections.push({
                    ...restListCtaButton,
                    ...restOverrideSettings,
                    content: (!!content && !restOverrideSettings.useDefaultSectionContent ? content : listSectionSettings),
                    isDefaultSection: true
                })
            });
        } else {
            sections.push({
                ...listItemSection,
                useDefaultSectionContent: false,
                isDefaultSection: false
            });
        }
    })

    return {
        sections: sections
    }
}

export function doppeListActionListItemWithCtaButtonsMigration(overrides: Partial<DoppeListActionSettings.ListItemWithCtaButtons>, hasSourceURL: boolean) {

    const ctaButtonsOverridesMap = overrides.ctaButtonsOverridesMap ?? {};
    objectForEachValue(ctaButtonsOverridesMap, (ctaButtonOverride) => {

        const ctaButtonSettings = assertDefined(doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
            ctaButton: ctaButtonOverride,
            hasSourceURL: hasSourceURL
        }));

        return {
            settings: ctaButtonSettings.ctaButton.settings,
            isVisible: ctaButtonOverride.isVisible,
            useDefaultContent: ctaButtonOverride.useDefaultContent,
        } satisfies  DoppeActionCTAButton.MultipleButtonOverride
    })

    const customCtaButtonsValue = overrides.customCtaButtons ? [...overrides.customCtaButtons] : [DoppeListActionSettingsCtaButtonAutoValue.ListTemplateButtons];
    const hasListTemplateButtons = customCtaButtonsValue.includes(DoppeListActionSettingsCtaButtonAutoValue.ListTemplateButtons);

    if (!hasListTemplateButtons) {
        customCtaButtonsValue.push(DoppeListActionSettingsCtaButtonAutoValue.ListTemplateButtons);
    }

    return {
        customCtaButtons: customCtaButtonsValue.map(listItemButton => {
            if (listItemButton === DoppeListActionSettingsCtaButtonAutoValue.ListTemplateButtons) {
                return listItemButton;
            } else {
                return assertDefined(doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                    ctaButton: listItemButton,
                    hasSourceURL: hasSourceURL
                })).ctaButton;
            }
        }),
        ctaButtonsOverridesMap: ctaButtonsOverridesMap,
    } satisfies DoppeListActionSettings.ListItemWithCtaButtons;
}

export function doppeListActionListItemCalculateItemButtons(ctaButtonsSettings: DoppeActionCtaButtonsSettings, itemSettings: DoppeListActionSettings.ListItemWithCtaButtons & DoppeListActionSettings.ListItemBaseSettings) {

    const ctaButtons: DoppeActionCTAButton.MultipleButtonWithOverride[] = [];


    itemSettings.customCtaButtons.forEach(listItemCtaButton => {
        if (listItemCtaButton === DoppeListActionSettingsCtaButtonAutoValue.ListTemplateButtons) {
            ctaButtonsSettings.ctaButtons.map(listCtaButton => {

                const override = itemSettings.ctaButtonsOverridesMap[listCtaButton.id];

                const {
                    settings,
                    ...restOverrideSettings
                } = override ?? {};

                const {
                    settings: listCtaButtonSettings,
                    ...restListCtaButton
                } = listCtaButton;

                ctaButtons.push({
                    ...restListCtaButton,
                    ...restOverrideSettings,
                    settings: caclulateCtaButtonOverrideSettings(listCtaButton, override),
                    isDefaultButton: true,
                    id: doppeActionCtaButtonCreateFromListAndOverrideId(itemSettings.id, listCtaButton.id)
                })
            })
        } else {
            ctaButtons.push({
                ...listItemCtaButton,
                useDefaultContent: false,
                isDefaultButton: false
            });
        }
    })

    return {
        ctaButtons: ctaButtons
    }
}


function caclulateCtaButtonOverrideSettings(ctaButton: DoppeActionCTAButton.MultipleButton, override?: DoppeActionCTAButton.MultipleButtonOverride) {

    if (ctaButton.settings.type === DoppeActionCTAType.Checkout) {

        return {
            ...ctaButton.settings,
            ...(!!override?.settings && !override.useDefaultContent ? override.settings : {}),
            checkoutSettings: ctaButton.settings.checkoutSettings
        }
    }
    else {
        return {
            ...ctaButton.settings,
            ...(!!override?.settings && !override.useDefaultContent ? override.settings : {})
        }
    }



}

export function doppeListActionListItemNormalizeSectionsOverridesMap(items: DoppeActionContentSection[], sectionsOverridesMap: Record<string, DoppeActionContentSection.Override>) {


    const newSectionsOverridesMap: Record<string, DoppeActionContentSection.Override> = {};

    items.forEach(sectionSettings => {
        newSectionsOverridesMap[sectionSettings.id] = {
            ...sectionsOverridesMap[sectionSettings.id],
            content: sectionSettings.content,
            isVisible: sectionSettings.isVisible,
        }
    })

    return newSectionsOverridesMap;
}

export function doppeListActionListItemNormalizeCtaButtonsOverridesMap(items: DoppeActionCTAButton.MultipleButton[], ctaButtonsOverridesMap: Record<string, DoppeActionCTAButton.MultipleButtonOverride>) {


    const newCtaButtonsOverridesMap: Record<string, DoppeActionCTAButton.MultipleButtonOverride> = {};

    items.forEach(ctaButton => {
        newCtaButtonsOverridesMap[ctaButton.id] = {
            ...ctaButtonsOverridesMap[ctaButton.id],
            settings: ctaButton.settings,
            isVisible: ctaButton.isVisible,
        }
    })

    return newCtaButtonsOverridesMap
}

export function doppeListActionWithMainDetailsGetMainImage(settings: DoppeListActionSettings.WithMainDetails, listItemImage: BymoPageMedia | null) {
    return hideableValueIsVisibleAndNotEqualValue(settings.mainImage, null) ? settings.mainImage : listItemImage;

}

export function doppeListActionWithMainDetailsReplaceMainImage(actionSettings: DoppeListActionSettings.WithMainDetails, externalImagesUrlToImageResourceMap: Map<string, WixImageResource>) {
    const mainImage = hideableValueGetValue(actionSettings.mainImage);
    if (mainImage) {
        actionSettings.mainImage = hideableValueModifyValue(actionSettings.mainImage, () => {
            return wixMediaResourceReplaceExternalImage(mainImage, externalImagesUrlToImageResourceMap)
        })
    }
}

export function doppeListActionGetDefaultListSettings<ItemSettings extends DoppeListActionSettings.ListItemBaseSettings>(overrides: Partial<DoppeListActionSettings<ItemSettings>>) {

    return {
        listDataItems: overrides.listDataItems ?? [],
        layout: overrides.layout ?? DoppeActionLayout.Column,
        itemShowDescription: overrides.itemShowDescription ?? true,
        itemShowTitle: overrides.itemShowTitle ?? true,
        itemShowImage: overrides.itemShowImage ?? true,
        itemShowTextOnImage: overrides.itemShowTextOnImage ?? false,
        mediaSize: overrides.mediaSize ?? doppeActionLayoutGetDefaultMediaSizeForLayout(overrides.layout ?? DoppeActionLayout.Column),
        mediaDesign: overrides.mediaDesign ?? DoppeActionLayoutMediaDesign.Default,
        mediaRatio: overrides.mediaRatio ?? doppeActionLayoutGetDefaultRatioForLayout(overrides.layout ?? DoppeActionLayout.Column),
        listItemLayoutType: overrides.listItemLayoutType ?? DoppeListActionItemLayout.Default,
        ...doppeActionContentSectionSettingsDefaultValuesMigration({
            sections: overrides.sections ?? []
        }),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
            ctaButtons: overrides.ctaButtons ?? []
        }, false),
    } satisfies DoppeListActionSettings;
}

export function doppeListActionGetDefaultSearchListSettings(overrides?: Partial<DoppeListActionSettings.WithSearchSettings>) {

    const {
        listSearchSettings,
    } = overrides ?? {}

    return {
        listSearchSettings: {
            ...doppeDtoActionGetDefaultSearchListSettingsProps(),
            ...listSearchSettings
        },
    } satisfies DoppeListActionSettings.WithSearchSettings;
}


export function doppeListActionListItemGetDefaultListItemSettingsMigrationForDummy(id: string) {
    return {
        ...doppeListActionListItemSettingsCreateDefault(id),
        isDummy: true
    };
}

export function doppeListActionListItemSettingsMigrateBaseSettings(item: PartiallyRequired<Partial<DoppeListActionSettings.ListItemBaseSettings>, 'id'>) {
    return {
        id: item.id,
        enabled: item.enabled ?? true,
        deleted: item.deleted ?? false,
        displayInSearch: item.displayInSearch ?? true,
        isDummy: item.isDummy ?? false,
    } satisfies DoppeListActionSettings.ListItemBaseSettings;
}


export function doppeListActionListItemSettingsCreateDefault(id: string) {
    return {
        id: id,
        enabled: true,
        deleted: false,
        displayInSearch: true,
        isDummy: false,
    } satisfies DoppeListActionSettings.ListItemBaseSettings;
}

/**
 * @deprecated I think that we should use doppeListActionListItemGetDefaultListItemSettingsMigration directly and then override the fields we want in the caller, instead of having this separate function.
 *  but for now, this is used in quite a few places so we will keep it until we can refactor all the callers.
 */
export function doppeListActionListItemGetDefaultListItemSettingsMigrationWithOverrides(id: string, overrides: Partial<DoppeListActionSettings.ListItemBaseSettings>) {
    return {
        ...doppeListActionListItemSettingsCreateDefault(id),
        ...overrides
    } satisfies DoppeListActionSettings.ListItemBaseSettings;
}

export function doppeListActionGetItemClickBehaviourDefaultListSettings(overrides?: Partial<DoppeListActionSettings.WithItemClickBehaviour>) {
    return {
        itemClickBehaviour: overrides?.itemClickBehaviour ?? DoppeActionListItemClickBehaviour.PreviewItemAsPopup,
        allowItemClickBehaviourPerItem: overrides?.allowItemClickBehaviourPerItem ?? false,
    } satisfies DoppeListActionSettings.WithItemClickBehaviour;
}

export function doppeListActionGetWithExternalDataSourceDefaultListSettings(overrides?: Partial<DoppeListActionSettings.WithExternalDataSourceSupport<any>>) {
    return {
        externalDataSource: overrides?.externalDataSource ?? null,
        externalDataSourceItemsCount: overrides?.externalDataSourceItemsCount ?? 4,
    } satisfies DoppeListActionSettings.WithExternalDataSourceSupport<any>;
}

export function doppeListActionGetMainDetailsDefaultSettings(
    options: {
        defaultTitle?: string;
    } = {},
    overrides?: Partial<DoppeListActionSettings.WithMainDetails>,
) {

    return {
        mainImage:  overrides?.mainImage ?? hideableValueCreateHidden(null),
        mainTitle: overrides?.mainTitle ?? hideableValueCreateHidden(options.defaultTitle ?? ''),
        ...doppeActionGetMediaViewDefaultSettingsMigration(overrides ?? {}),
    } satisfies DoppeListActionSettings.WithMainDetails;
}

export function convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction(
    actionSettings: Partial<DoppeListActionSettings & DoppeListActionSettings.WithCtaButton & DoppeListActionSettings.DeprecatedFields>
) {
    return getDefaultDoppeActionCtaButtonSettings({
        buttonText: actionSettings.buttonText,
        url: actionSettings.buttonUrl ?? '',
        showButton: actionSettings.showButton !== undefined ? actionSettings.showButton : false,
    });
}

export function doppeListActionSettingsGetListItemCtaButtonsDefaultSettings() {
    return {
        customCtaButtons: [DoppeListActionSettingsCtaButtonAutoValue.ListTemplateButtons],
        ctaButtonsOverridesMap: {},
    } as DoppeListActionSettings.ListItemWithCtaButtons;
}

export function doppeListActionSettingsGetListItemSectionsDefaultSettings() {
    return {
        customSections: [DoppeListActionSettingsContentSectionAutoValue.ListTemplateSections],
        sectionsOverridesMap: {},
    } satisfies DoppeListActionSettings.ListItemWithSections;
}


export function doppeListActionSettingsFilterHiddenListItemsFromActionView<T extends DoppeListActionSettings.ListItemBaseSettings>(items?: T[]) {
    return items?.filter(doppeListActionSettingsIsListItemVisibleInAction) ?? [];
}

export function doppeListActionSettingsFilterDeletedListItemsFromActionView<T extends DoppeListActionSettings.ListItemBaseSettings>(items?: T[]) {
    return items?.filter(doppeListActionSettingsIsListItemIsNotDeleted) ?? [];
}


export function doppeListActionSettingsFilterHiddenListItemsFromSearchResults<T extends DoppeListActionSettings.ListItemBaseSettings>(items?: T[]) {
    return items?.filter(doppeListActionSettingsIsListItemVisibleInSearchResults) ?? [];
}

export function doppeListActionSettingsIsListItemVisibleInAction<T extends DoppeListActionSettings.ListItemBaseSettings>(item: T) {
    return item.enabled && !item.deleted;
}

export function doppeListActionSettingsIsListItemDummy<T extends DoppeListActionSettings.ListItemBaseSettings>(item: T) {
    return item.isDummy;
}

export function doppeListActionSettingsIsListItemHiddenInActionAndNotDeleted<T extends DoppeListActionSettings.ListItemBaseSettings>(item: T) {
    return !item.enabled && !item.deleted;
}

export function doppeListActionSettingsIsListItemVisibleInSearchResults<T extends DoppeListActionSettings.ListItemBaseSettings>(item: T) {
    return doppeListActionSettingsIsListItemVisibleInAction(item) && item.displayInSearch;
}

export function doppeListActionSettingsIsListItemIsDeleted<T extends DoppeListActionSettings.ListItemBaseSettings>(item: T) {
    return item.deleted;
}

export function doppeListActionSettingsIsListItemIsNotDeleted<T extends DoppeListActionSettings.ListItemBaseSettings>(item: T) {
    return !item.deleted;
}

export function doppeListActionSettingsIsListItemIsVisible<T extends DoppeListActionSettings.ListItemBaseSettings>(item: T) {
    return item.enabled;
}

export function doppeListActionSettingsIsLargeItemSizeItemLayout(layout: DoppeListActionItemLayout) {
    return layout === DoppeListActionItemLayout.FullDetails || layout === DoppeListActionItemLayout.FullDetailsWithShowMore;
}

export function doppeListActionSettingsGetListItemItemClickBehaviourDefaultSettings() {
    return {
        itemClickBehaviour: hideableValueCreateHidden(DoppeActionListItemClickBehaviour.PreviewItemAsPopup),
    } satisfies DoppeListActionSettings.ListItemWithItemClickBehaviour;
}

export function doppeListActionListItemWithItemClickBehaviourMigration(overrides?: Partial<DoppeListActionSettings.ListItemWithItemClickBehaviour>) {
    return {
        itemClickBehaviour: overrides?.itemClickBehaviour ?? hideableValueCreateHidden(DoppeActionListItemClickBehaviour.PreviewItemAsPopup),
    } satisfies DoppeListActionSettings.ListItemWithItemClickBehaviour;
}

/**
 * Resolves the effective click behaviour for a list item, considering the per-item override.
 */
export function doppeListActionSettingsResolveListItemEffectiveClickBehaviour(
    listItemSettings: DoppeListActionSettings.ListItemWithItemClickBehaviour,
    listActionSettings: DoppeListActionSettings.WithItemClickBehaviour
): DoppeActionListItemClickBehaviour {
    return doppeActionListItemResolveEffectiveClickBehaviour(
        listItemSettings.itemClickBehaviour,
        listActionSettings.itemClickBehaviour
    );
}


